<template>
  <div
    ref="recentlyViewed"
    class="products-recently-viewed"
    :class="{ 'is-loading' : state !== 'ready' }"
  >
    <heading
      v-if="headingText"
      class="products-recently-viewed__heading"
      :type="headingType"
      :text="headingText"
    />

    <product-carousel
      :products="products"
      :tracking-parameters="trackingParameters"
      :section-id="liquid.sectionId"
      :slides-per-view="{
        mobile: 1.25,
        tablet: 2.22,
        desktop: 3,
        wide: 4
      }"
    />
  </div>
</template>

<script>

/**
 * Section: Recently Viewed Products
 * ------------------------------------------------------------------------------
 * - A carousel of 'Recently Viewed' products cards.
 * @param {String} [headingText] - Heading Text to display.
 * @param {String} [headingType] - Theme Settings for h1-h6 values.
 * @param {String} [liquid.sectionId] - Section's Liquid ID.
 * @param {Object} [trackingParameters] - Object of parameters, typically added to events (Bloomreach, Attraqt, Google).
 * @param {String} [trackingParameters.section] - Unique name of the section.
 */
import { getProduct } from '~/core/get-product'

import { isVisible, setIntersectionObserver } from '~/helpers/dom'

import { storageNamespace } from '~/helpers/storage-namespaces'
// import { stringToHandle } from '~/helpers/strings'

import Heading from '~components/heading/heading'
import ProductCarousel from '~components/product-carousel/product-carousel'

export default {
  name: 'ProductsRecentlyViewed',

  components: {
    Heading,
    ProductCarousel,
  },

  props: {
    headingText: {
      type: String,
      default: '',
    },
    headingType: {
      type: String,
      default: 'h2',
    },
    trackingParameters: {
      type: Object,
      default: () => ({}),
    },
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
  },

  data() {
    return {
      state: 'loading',
      products: [
        {
          key: 0,
        },
        {
          key: 1,
        },
        {
          key: 2,
        },
        {
          key: 3,
        },
      ],
    }
  },

  mounted () {
    this.initializeSectionObserver()
  },

  methods: {

    /**
     * Initializes the observer for the recently viewed section.
     * - if the section is already visible set the products immediately.
     */
    initializeSectionObserver() {
      const targetElement = this.$refs.recentlyViewed

      if (isVisible(targetElement)) {
        this.setProducts()
        return
      }

      setIntersectionObserver({
        callback: this.handleSectionIntersection,
        root: targetElement.parentNode,
        rootMargin: '20px 0px',
        element: targetElement,
        threshold: 0.1,
      })
    },

    /**
     * Handle event when a section is scrolled into view.
     * @param {Array} entries - Array of Intersection Observer entries.
     * @param {Object} observer - Intersection Observer.
     */
    handleSectionIntersection(entries, observer) {
      entries.forEach(entry => {
        if (!entry.isIntersecting) {
          return
        }

        /**
         * All of the products have loaded.
         */
        if (this.state === 'ready') {
          observer.disconnect()
          return
        }

        this.setProducts()
      })
    },

    /**
     * Set the products that will be displayed in the component.
     */
    async setProducts() {
      try {

        /**
         * If in the Theme Editor, don't query and render products.
         * - GQL can also be unreliable within the Theme Editor, so prevent
         *   queries in case no products load and the section hides itself.
         */
        if (mint.request.designMode) {
          return
        }

        let handles = JSON.parse(
          localStorage.getItem(storageNamespace.recentlyViewedProducts) || '[]',
        )

        /**
         * If on the PDP, do not show the current product in the carousel.
         */
        if (mint.request.pageType === 'product') {
          handles = handles.filter((handle) => {
            return Boolean(mint.request.path !== `/products/${handle}`)
          })
        }

        if (!handles.length) {
          this.destroyComponent()
          return
        }

        const queries = handles.map((handle) => {
          return getProduct({ handle })
        })

        const response = await Promise.allSettled(queries)

        /**
         * If the query fails, it's likely that the product isn't
         * available on the Storefront API channel.
         */
        this.products = response.map((promise) => {
          const product = promise?.value

          if (
            promise.status !== 'fulfilled' ||
            !product?.handle ||
            product?.restricted
          ) {
            return false
          }

          /**
           * Filter out unavailable and market-restricted products.
           */

          return promise.value
        }).filter(Boolean)

        if (!this.products.length) {
          this.destroyComponent()
          return
        }

        this.state = 'ready'
        this.handleTrackingEvents()

      } catch (error) {
        mint.Log('warning', 'Error fetching Recently Viewed products', error)
      }
    },

    /**
     * Emit 'View' events to relevant event trackers (Bloomreach, Google etc.)
     */
    handleTrackingEvents() {
      // if (!mint.customer.canBeTracked) {
      //   return
      // }

      // const attributes = JSON.parse(JSON.stringify(this.trackingParameters))

      // window.console.log('Track Recently Viewed View here...', {
      //   handle: stringToHandle(attributes.section),
      //   section: attributes.section,
      // })
    },

    /**
     * Remove and unmount the component.
     * - Note, just removes the parent element.
     * - Ideally this should unmount the entire component.
     */
    destroyComponent() {
      const parentSection = document.querySelector(
        '[data-mint-section="products-recently-viewed"]',
      )

      parentSection.remove()
    },
  },
}
</script>

<style src="./products-recently-viewed.scss" lang="scss"></style>
